import React, { useState, useMemo, useEffect } from 'react';
import { Text, Box, Heading, Select as ChakraSelect, VStack, HStack, Button, useToast, Collapse, IconButton } from '@chakra-ui/react';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import { useContext } from 'react';
import { CHALLENGE_VIRTUAL_STUDY_GROUP_ID } from './FileBlock';
import { useIsMobile } from '../../../../../hooks/isMobile';
import useInteractiveQuizAnswersFromActiveLicenses from '../../../hooks/useInteractiveQuizAnswersFromActiveLicenses';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import axios from 'axios';
import { NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { StemiBeatLoader } from '../../../../../components/Loader';

interface QuizAnswer {
    answer_id: string;
    answers: string;
    school_name: string | null;
    school_id: string | null;
    student_name: string | null;
    student_username: string | null;
    student_id: string;
    study_group_id: string;
    unique_quiz_id: string;
    program_id: string | null;
    program_name: string | null;
    is_correct: boolean;
    points: number;
    max_points: number;
    created_at: string;
    manually_graded: boolean;
    actual_student_school_name: string | null;
}

interface AdminInteractiveQuizAnswersProps {
    unique_quiz_id: string;
    question_type: string;
    answers: any;
    max_points: number;
    question_id?: string;
}

const AdminInteractiveQuizAnswers: React.FC<AdminInteractiveQuizAnswersProps> = ({
    unique_quiz_id,
    question_type,
    answers,
    max_points,
    question_id
}) => {
    const user = useContext(AuthContext);
    const [selectedSchoolId, setSelectedSchoolId] = useState<string>('');
    const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
    const [points, setPoints] = useState<number>(0);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { quizAnswers, isLoading, error } = useInteractiveQuizAnswersFromActiveLicenses(unique_quiz_id, question_id);
    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const toast = useToast();

    if (!user?.roles.includes(Role.staff)) {
        return null;
    }

    // Get sorted unique schools from quiz answers
    const schools = useMemo(() => {
        const schoolMap = new Map<string, { id: string; name: string }>();
        quizAnswers.forEach((answer: QuizAnswer) => {
            if (answer.school_id && answer.school_name) {
                schoolMap.set(answer.school_id, {
                    id: answer.school_id,
                    name: answer.school_name
                });
            }
        });

        // Add challenge virtual school if there are any challenge answers
        const hasChallengeAnswers = quizAnswers.some(
            (answer: QuizAnswer) => answer.study_group_id === CHALLENGE_VIRTUAL_STUDY_GROUP_ID
        );
        if (hasChallengeAnswers) {
            schoolMap.set(CHALLENGE_VIRTUAL_STUDY_GROUP_ID, {
                id: CHALLENGE_VIRTUAL_STUDY_GROUP_ID,
                name: 'Challenge - virtual school'
            });
        }

        return Array.from(schoolMap.values()).sort((a, b) =>
            a.name.localeCompare(b.name)
        );
    }, [quizAnswers]);

    // Set the first school as selected when schools are loaded
    useEffect(() => {
        if (schools.length > 0 && !selectedSchoolId && schools[0]?.id) {
            setSelectedSchoolId(schools[0].id);
        }
    }, [schools, selectedSchoolId]);

    // Get student options for the selected school
    const studentOptions = useMemo(() => {
        const filteredAnswers = quizAnswers.filter((answer: QuizAnswer) => {
            if (selectedSchoolId === CHALLENGE_VIRTUAL_STUDY_GROUP_ID) {
                return answer.study_group_id === CHALLENGE_VIRTUAL_STUDY_GROUP_ID;
            }
            return answer.school_id === selectedSchoolId;
        });

        const studentMap = new Map<string, { value: string; label: string }>();
        filteredAnswers.forEach((answer: QuizAnswer) => {
            if (answer.student_name) {
                studentMap.set(answer.student_id, {
                    value: answer.student_id,
                    label: answer.student_name
                });
            }
        });

        return Array.from(studentMap.values()).sort((a, b) =>
            a.label.localeCompare(b.label)
        );
    }, [quizAnswers, selectedSchoolId]);

    // filter answers by selected school and student
    const filteredAnswers = useMemo(() => {
        let filtered = quizAnswers.filter((answer: QuizAnswer) => {
            if (selectedSchoolId === CHALLENGE_VIRTUAL_STUDY_GROUP_ID) {
                return answer.study_group_id === CHALLENGE_VIRTUAL_STUDY_GROUP_ID;
            }
            return answer.school_id === selectedSchoolId;
        });

        if (selectedStudentId) {
            filtered = filtered.filter((answer: QuizAnswer) =>
                answer.student_id === selectedStudentId
            );
        }

        return filtered;
    }, [quizAnswers, selectedSchoolId, selectedStudentId]);

    const handleGradeAnswer = async (answer: QuizAnswer) => {
        try {
            await axios.patch(`/api/v1/syllabus/interactive_quiz_answers/`, {
                study_group_id: answer.study_group_id,
                student_id: answer.student_id,
                question_id: answer.unique_quiz_id,
                answer_id: answer.answer_id,
                points: points
            }, {
                headers: {
                    'X-CSRFToken': JSON.parse(
                        document.getElementById('csrf_token')!.innerHTML,
                    ),
                },
            });

            toast({
                title: t('plugin.interactiveQuiz.gradeSubmitted'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: t('plugin.interactiveQuiz.gradeError'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    if (isLoading) {
        return <StemiBeatLoader />;
    }

    if (error) {
        return <Text color="red.500">Error loading answers: {error}</Text>;
    }

    return (
        <Box mt={8} p={4} borderWidth="1px" borderRadius="lg" width="100%">
            <HStack justify="space-between" mb={4}>
                <Heading size="md">Admin View: Quiz Answers</Heading>
                <IconButton
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                    icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    onClick={() => setIsOpen(!isOpen)}
                    variant="ghost"
                />
            </HStack>

            <Collapse in={isOpen}>
                <VStack spacing={4} align="stretch" p="1rem">
                    <Box>
                        <Text mb={2}>School:</Text>
                        <ChakraSelect
                            value={selectedSchoolId}
                            onChange={(e) => {
                                setSelectedSchoolId(e.target.value);
                                setSelectedStudentId(null);
                            }}
                        >
                            {schools.map((school) => (
                                <option key={school.id} value={school.id}>
                                    {school.name}
                                </option>
                            ))}
                        </ChakraSelect>
                    </Box>

                    {selectedSchoolId && (
                        <Box>
                            <Text mb={2}>Select Student:</Text>
                            <Select
                                styles={{
                                    container: (provided: any) => ({
                                        ...provided,
                                        minWidth: '350px',
                                        textAlign: 'left',
                                    }),
                                }}
                                isClearable
                                placeholder={t('plugin.interactiveQuiz.selectStudent')}
                                noOptionsMessage={() => t('plugin.interactiveQuiz.noStudents')}
                                options={studentOptions}
                                value={studentOptions.find((o) => o.value === selectedStudentId)}
                                onChange={(value: { value: string; label: string } | null) => {
                                    setSelectedStudentId(value?.value || null);
                                }}
                                menuPlacement="auto"
                                menuPortalTarget={document.body}
                            />
                        </Box>
                    )}

                    <Box
                        maxH="700px"
                        overflowY="auto"
                        css={{
                            '&::-webkit-scrollbar': {
                                width: '4px',
                            },
                            '&::-webkit-scrollbar-track': {
                                width: '6px',
                                background: 'gray.100',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'gray.400',
                                borderRadius: '24px',
                            },
                        }}
                    >
                        {filteredAnswers.length === 0 ? (
                            <Text>No answers found{selectedSchoolId ? ' for selected school' : ''}</Text>
                        ) : (
                            filteredAnswers.map((answer: QuizAnswer, index: number) => (
                                <Box
                                    key={`${answer.answer_id}-${index}`}
                                    p={4}
                                    mb={4}
                                    borderWidth="1px"
                                    borderRadius="md"
                                    bg="gray.50"
                                >
                                    <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', margin: '4px', justifyContent: isMobile ? 'flex-start' : 'space-between' }}>
                                        <span style={isMobile ? {} : { width: '260px', maxWidth: '260px' }}>
                                            <Text fontWeight="medium">{answer.student_name}</Text>
                                            {!answer.student_name && <Text fontSize="sm">Username: {answer.student_username}</Text>}
                                            {answer.program_name && (
                                                <Text fontSize="sm">Program: {answer.program_name}</Text>
                                            )}
                                            {answer.actual_student_school_name && (
                                                <Text fontSize="sm">School: {answer.actual_student_school_name}</Text>
                                            )}
                                            <Text fontSize="sm">Points: {answer.points}/{max_points}</Text>
                                            <Text fontSize="sm">Correct: {answer.is_correct ? 'Yes' : 'No'}</Text>
                                            <Text fontSize="sm">Manually Graded: {answer.manually_graded ? 'Yes' : 'No'}</Text>
                                        </span>
                                        <Box p={4} borderWidth="1px" borderRadius="md" bg="white" maxWidth={300}>
                                            {question_type === 'ABCDQuestion' ? (
                                                <Text>
                                                    {answer.answers.split(',').map((key: string) => {
                                                        const nkey = Number(key);
                                                        return answers[nkey]?.answer || '';
                                                    }).join(', ')}
                                                </Text>
                                            ) : (
                                                <Text>{answer.answers}</Text>
                                            )}
                                        </Box>
                                    </div>
                                    {!answer.manually_graded && (
                                        <HStack mt={4} spacing={4}>
                                            <NumberInput
                                                size="sm"
                                                maxW={24}
                                                min={0}
                                                max={max_points}
                                                value={points}
                                                onChange={(_, value) => setPoints(value)}
                                            >
                                                <NumberInputField />
                                                <NumberInputStepper>
                                                    <NumberIncrementStepper />
                                                    <NumberDecrementStepper />
                                                </NumberInputStepper>
                                            </NumberInput>
                                            <Button
                                                size="sm"
                                                colorScheme="blue"
                                                onClick={() => handleGradeAnswer(answer)}
                                            >
                                                Grade Answer
                                            </Button>
                                        </HStack>
                                    )}
                                </Box>
                            ))
                        )}
                    </Box>
                </VStack>
            </Collapse>
        </Box>
    );
};

export default AdminInteractiveQuizAnswers; 